import type { MusicSubscriptionOffer } from '@amp/musickit';
import { loggerFor } from '@amp/web-apps-logger';
import { JWT } from '~/config/media-api';
import type { User } from '~/utils/user';

const WEB_OFFER_TYPE = 'Individual';

export enum SubscriberType {
    NotActive = 'notActive',
    Voice = 'voice',
    Premium = 'premium',
}

export interface UpsellOfferDetails {
    readonly isFreeTrial: boolean;
    readonly basePriceString: string;
    readonly introPeriod: number;
    readonly introPrice: string;
    readonly isIntroOffer?: boolean;
}

export type MusicSubscription = {
    isNotEligibleForFreeTrial: boolean;
    serviceBeginsTimestamp: number;
    isInFreePeriod: boolean;
    status: string; // can be an ENUM ("Enabled", "Disabled")
    reason: string; // can be an ENUM ("MEMBER", "??")
    isPurchaser: boolean;
    expirationDate: number;
    isFamilySubscription: boolean;
    isInFreeTrial: boolean;
    initialPurchaseTimestamp: number;
    isAutoRenewEnabled: boolean;
};

export function makeUpsellOfferDetails(
    offers: MusicSubscriptionOffer[],
): UpsellOfferDetails | null {
    const subscriptionOffer = offers?.filter(
        (offer) => offer.subscriptionType === WEB_OFFER_TYPE,
    )[0];
    return subscriptionOffer
        ? {
              isFreeTrial: isFreeTrial(subscriptionOffer),
              introPeriod: getIntroPeriod(subscriptionOffer),
              introPrice: subscriptionOffer?.introOfferPrice,
              basePriceString: subscriptionOffer?.price,
              isIntroOffer: subscriptionOffer?.isIntroOffer,
          }
        : ({} as UpsellOfferDetails);
}

function getIntroPeriod(offer: MusicSubscriptionOffer): number {
    const offerPeriod = offer?.introPeriod; // PXM
    if (!offerPeriod) {
        return;
    }
    const [, period] = offerPeriod.match(/^P(\d+)M$/) || []; // Validate we get correct period string P3M - > 3
    return parseInt(period);
}

function isFreeTrial(offer: any) {
    if (!offer?.isIntroOffer) {
        return false;
    }

    const numericPrice =
        typeof offer?.introOfferPrice === 'string'
            ? offer?.introOfferPrice?.replace(/\D/g, '')
            : offer?.introOfferPrice;
    return parseFloat(numericPrice) === 0;
}

export async function getMusicSubscriptionStatus(): Promise<{
    account: User;
    music: MusicSubscription;
    status: number; // can be an ENUM (0, ??)
}> {
    // Switch over to commerceKit fetch API
    // rdar://121613538 (CommerceKit: Implement fetch API)
    const host = 'https://musicstatus.music.apple.com';
    const path = '/v1/web/status';
    const request = {
        url: `${host}${path}`,
        headers: {},
        method: 'GET',
    };
    request.headers['Authorization'] = `Bearer ${JWT}`;

    return fetch(request.url, {
        credentials: 'include',
        ...request,
    })
        .then((res) => res.json())
        .catch((e) => {
            loggerFor('subscriptions').error(
                'error while getting userInfo:',
                e,
            );
        });
}
